import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../../data/apiFetch";
import { getPackageTemplates, suggestPrice, formatPackageMeta } from "../../lib/packageTemplates";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]";

const EMPTY = {
  id: null,
  title: "",
  audience: "",
  outcomes: [""],
  format: "single",
  sessions: 2,
  durationMin: 60,
  deliveryDays: 5,
  price: "",
  requirements: "",
  isActive: true,
};

/**
 * Specialist-side editor for fixed-price service packages.
 * Props: sphere (profile sphere key, used for templates), rate (hourly price,
 * used to suggest a package price).
 */
export default function ServicePackagesEditor({ sphere = "", rate = null }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("en") ? "en" : "uk";

  const [items, setItems] = useState([]);
  const [max, setMax] = useState(6);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const templates = useMemo(() => getPackageTemplates(sphere, lang), [sphere, lang]);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch("/api/packages/mine");
      setItems(Array.isArray(data?.items) ? data.items : []);
      if (data?.max) setMax(data.max);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const errText = (code) => {
    const key = `packages.errors.${code}`;
    const s = t(key);
    return s === key ? t("packages.errors.generic") : s;
  };

  const startNew = (tpl = null) => {
    setError("");
    if (!tpl) return setForm({ ...EMPTY });
    setForm({
      ...EMPTY,
      title: tpl.title,
      audience: tpl.audience || "",
      outcomes: tpl.outcomes?.length ? [...tpl.outcomes] : [""],
      format: tpl.format || "single",
      sessions: tpl.sessions || 2,
      durationMin: tpl.durationMin || 60,
      deliveryDays: tpl.deliveryDays || 5,
      price: suggestPrice(rate, tpl.hours),
      requirements: tpl.requirements || "",
    });
  };

  const edit = (p) => {
    setError("");
    setForm({
      ...EMPTY,
      ...p,
      outcomes: p.outcomes?.length ? [...p.outcomes] : [""],
      sessions: p.sessions || 2,
      durationMin: p.durationMin || 60,
      deliveryDays: p.deliveryDays || 5,
      audience: p.audience || "",
      requirements: p.requirements || "",
    });
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setError("");
    const outcomes = form.outcomes.map((o) => o.trim()).filter(Boolean);
    if (form.title.trim().length < 3) return setError(errText("TITLE_REQUIRED"));
    if (!outcomes.length) return setError(errText("OUTCOMES_REQUIRED"));
    if (!Number(form.price)) return setError(errText("PRICE_REQUIRED"));

    const body = JSON.stringify({ ...form, outcomes, price: Number(form.price) });
    setBusy(true);
    try {
      if (form.id) await apiFetch(`/api/packages/mine/${form.id}`, { method: "PUT", body });
      else await apiFetch("/api/packages/mine", { method: "POST", body });
      setForm(null);
      await load();
    } catch (e) {
      setError(errText(e?.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("packages.deleteConfirm", { title: p.title }))) return;
    try {
      await apiFetch(`/api/packages/mine/${p.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(errText(e?.message));
    }
  };

  const toggleActive = async (p) => {
    try {
      await apiFetch(`/api/packages/mine/${p.id}`, {
        method: "PUT",
        body: JSON.stringify({ ...p, isActive: !p.isActive }),
      });
      await load();
    } catch (e) {
      setError(errText(e?.message));
    }
  };

  const canAdd = items.length < max;

  return (
    <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-unbounded text-xl font-semibold">{t("packages.title")}</h2>
          <p className="mt-2 max-w-xl text-sm text-white/65">{t("packages.subtitle")}</p>
        </div>
        <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/70 ring-1 ring-white/10">
          {items.length} / {max}
        </span>
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-white/60">{t("common.loading", "…")}</div>
      ) : (
        <div className="mt-4 space-y-3">
          {/* Existing packages */}
          {items.map((p) => (
            <div key={p.id} className={`rounded-xl px-4 py-3 ring-1 ${p.isActive ? "bg-white/5 ring-white/10" : "bg-white/[0.02] ring-white/5 opacity-70"}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{p.title}</div>
                  <div className="mt-0.5 text-xs text-white/55">
                    {formatPackageMeta(t, p)} · {Number(p.price).toLocaleString("uk-UA")} ₴
                    {!p.isActive && <> · {t("packages.hidden")}</>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2 text-xs">
                  <button type="button" onClick={() => edit(p)} className="rounded-lg border border-white/15 px-2.5 py-1 hover:bg-white/10">
                    {t("packages.edit")}
                  </button>
                  <button type="button" onClick={() => toggleActive(p)} className="rounded-lg border border-white/15 px-2.5 py-1 hover:bg-white/10">
                    {p.isActive ? t("packages.hide") : t("packages.show")}
                  </button>
                  <button type="button" onClick={() => remove(p)} className="rounded-lg border border-red-400/30 px-2.5 py-1 text-red-200 hover:bg-red-500/10">
                    {t("packages.delete")}
                  </button>
                </div>
              </div>
            </div>
          ))}

          {items.length === 0 && !form && (
            <div className="rounded-xl bg-white/5 px-4 py-3 text-sm text-white/60 ring-1 ring-white/10">{t("packages.empty")}</div>
          )}

          {/* Templates + add */}
          {!form && canAdd && (
            <div className="space-y-2">
              <div className="text-sm text-white/70">{t("packages.startFromTemplate")}</div>
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => (
                  <button
                    key={tpl.key}
                    type="button"
                    onClick={() => startNew(tpl)}
                    className="rounded-full border border-[#7269e3]/40 bg-[#7269e3]/10 px-3 py-1.5 text-xs text-[#d8d4ff] hover:bg-[#7269e3]/20"
                  >
                    + {tpl.title}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => startNew()}
                  className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
                >
                  + {t("packages.fromScratch")}
                </button>
              </div>
            </div>
          )}
          {!form && !canAdd && <div className="text-xs text-white/50">{t("packages.limitReached", { max })}</div>}

          {/* Form */}
          {form && (
            <div className="space-y-4 rounded-xl bg-white/[0.03] p-4 ring-1 ring-[#7269e3]/30">
              <div className="text-xs text-white/50">{t("packages.editHint")}</div>

              <div>
                <label className="mb-1 block text-sm text-white/80">{t("packages.f.title")}</label>
                <input value={form.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} className={inputCls} placeholder={t("packages.ph.title")} />
              </div>

              <div>
                <label className="mb-1 block text-sm text-white/80">{t("packages.f.audience")}</label>
                <input value={form.audience} maxLength={160} onChange={(e) => set({ audience: e.target.value })} className={inputCls} placeholder={t("packages.ph.audience")} />
              </div>

              <div>
                <label className="mb-1 block text-sm text-white/80">{t("packages.f.outcomes")}</label>
                <div className="space-y-2">
                  {form.outcomes.map((o, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        value={o}
                        maxLength={160}
                        onChange={(e) => set({ outcomes: form.outcomes.map((x, j) => (j === i ? e.target.value : x)) })}
                        className={inputCls}
                        placeholder={t("packages.ph.outcome")}
                      />
                      {form.outcomes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => set({ outcomes: form.outcomes.filter((_, j) => j !== i) })}
                          className="rounded-lg px-2 text-white/50 hover:text-white"
                          aria-label={t("packages.removeOutcome")}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {form.outcomes.length < 6 && (
                  <button type="button" onClick={() => set({ outcomes: [...form.outcomes, ""] })} className="mt-2 text-xs text-[#b9b3ff] hover:underline">
                    + {t("packages.addOutcome")}
                  </button>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("packages.f.format")}</label>
                  <select value={form.format} onChange={(e) => set({ format: e.target.value })} className={inputCls}>
                    <option value="single">{t("packages.format.single")}</option>
                    <option value="multi">{t("packages.format.multi")}</option>
                    <option value="async">{t("packages.format.async")}</option>
                  </select>
                </div>
                {form.format === "multi" && (
                  <div>
                    <label className="mb-1 block text-sm text-white/80">{t("packages.f.sessions")}</label>
                    <input type="number" min="2" max="50" value={form.sessions} onChange={(e) => set({ sessions: e.target.value })} className={inputCls} />
                  </div>
                )}
                {form.format !== "async" ? (
                  <div>
                    <label className="mb-1 block text-sm text-white/80">{t("packages.f.durationMin")}</label>
                    <select value={form.durationMin} onChange={(e) => set({ durationMin: Number(e.target.value) })} className={inputCls}>
                      {[30, 45, 60, 90, 120, 180, 240].map((m) => (
                        <option key={m} value={m}>{t("packages.minutes", { n: m })}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="mb-1 block text-sm text-white/80">{t("packages.f.deliveryDays")}</label>
                    <input type="number" min="1" max="90" value={form.deliveryDays} onChange={(e) => set({ deliveryDays: e.target.value })} className={inputCls} />
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("packages.f.price")}</label>
                  <input type="number" min="1" value={form.price} onChange={(e) => set({ price: e.target.value })} className={inputCls} placeholder="₴" />
                  {Number(rate) > 0 && (
                    <div className="mt-1 text-xs text-white/45">{t("packages.rateHint", { rate })}</div>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm text-white/80">{t("packages.f.requirements")}</label>
                <input value={form.requirements} maxLength={400} onChange={(e) => set({ requirements: e.target.value })} className={inputCls} placeholder={t("packages.ph.requirements")} />
              </div>

              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} className="h-4 w-4 accent-[#7269e3]" />
                {t("packages.f.isActive")}
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  className="rounded-xl bg-[#7269e3] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#5f56d6] disabled:opacity-50"
                >
                  {busy ? t("packages.saving") : t("packages.save")}
                </button>
                <button
                  type="button"
                  onClick={() => { setForm(null); setError(""); }}
                  className="rounded-xl border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:bg-white/10"
                >
                  {t("common.cancel", "Cancel")}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-200 ring-1 ring-red-400/20">{error}</div>
          )}
        </div>
      )}
    </section>
  );
}
